/* By Layla Saleh - "In Demand": the first frame arrives centred, then glides
 * to the left edge before the page moves on.
 *
 * Additive. Delete the one <script> tag in sections/ui-overlays.liquid and
 * the section behaves exactly as it does on the live site.
 *
 * THE MOTION
 * ----------
 * While the section scrolls into view, the row is pushed right so the first
 * frame sits in the centre of the screen. When the section reaches the top
 * it is held, and scrolling slides the row left until the first frame is
 * flush with the left edge - the carousel's own natural first position. Then
 * the hold releases and the page carries on.
 *
 * Ending exactly on the carousel's natural position matters: Swiper never
 * has to move at all. It sits on its first slide the whole time, and at the
 * end there is nothing to hand over, so nothing can jump.
 *
 * The theme already ships GSAP 3.12.7 and ScrollTrigger; nothing new loads.
 *
 * Picking the right carousel: .popular-products-section contains 73
 * .swiper-slide elements, because every product card has its own small image
 * slider. Only 9 are products. The outer carousel is found via
 * .section__swiper-wrapper, unique inside the section.
 *
 * ---------------------------------------------------------------------------
 * WHY IT USED TO SNAP - MEASURED FROM A RECORDING
 * ---------------------------------------------------------------------------
 * Tracking a screen recording at 30 fps showed the hold itself was steady (0 px
 * of vertical movement) but the row was jumping sideways: 240+ px forward in
 * one frame, 60 px back, 220 px forward. The theme's Swiper has
 * mousewheel:{enabled:true}, speed:1100, and resize/update handlers that call
 * slideTo(activeIndex, 0), snapping its `transform` to the slide it believes
 * it is on. Earlier versions wrote that same `transform` - two controllers
 * fighting over one property, with pinning itself triggering a resize.
 *
 * Now the offset lives in the CSS `translate` property, which the browser
 * composes WITH `transform` but which Swiper never touches. Swiper stays on
 * its first slide, so its snaps land where it already is. Its 1100 ms
 * transition no longer applies either - the wrapper's transition-property is
 * `transform` only. While the row is offset, Swiper's wheel, swipe and arrows
 * are paused so it cannot move underneath.
 *
 * THE THEME REBUILDS THE CAROUSEL ON EVERY RESIZE
 * -----------------------------------------------
 * assets/global.js listens for window resize and, every time, calls
 * destroy(true, true) on every .section-slider carousel and builds a new one.
 * That throws away the Swiper instance and strips the row's inline styles -
 * our offset included. Phones fire resize whenever the address bar hides or
 * shows during a scroll, so this can land mid-hold. Hence:
 *   - the carousel is always looked up fresh (outer.swiper), never cached;
 *   - a resize listener puts the offset and the pause back. The theme's
 *     listener was registered first, so this one runs straight after it in
 *     the same task, before anything is painted.
 *
 * SETTINGS DEPEND ON WHAT IS DRIVING THE SCROLL
 * ---------------------------------------------
 * Mouse with assets/smooth-scroll.js on: Lenis glides the scroll and drives
 * ScrollTrigger on the same frame, so the row follows closely (scrub 0.2)
 * and the pin needs no anticipation.
 *
 * Phone or tablet: Lenis is set up with syncTouch:false, so a finger scroll
 * stays native even with smooth scrolling on. Native scroll is already a
 * glide, so scrub stays 0.2, but it runs ahead of the page on its own thread,
 * which is what anticipatePin covers - without it the hold engaged with a
 * measured 20 px snap.
 *
 * Mouse without smooth scrolling: the wheel moves in notches, so the row is
 * given a full second to glide (scrub 1), and anticipatePin as above.
 *
 * WAITING FOR THE CAROUSEL TO SETTLE
 * ----------------------------------
 * Swiper picks how many slides fit (2.2 below 990 px, 3.2 above) from
 * breakpoints, and re-measures on resize. If this script measures before that
 * has happened, a slide can briefly read as the full container width - which
 * makes the centring offset zero. A zero offset means "not yet", not "nothing
 * to do": it keeps retrying for ten seconds, and again on window load and on
 * resize, and only marks itself done once it has actually built.
 *
 * WHERE IT DELIBERATELY DOES NOTHING
 * ----------------------------------
 * - prefers-reduced-motion: reduce.
 * - When the first frame is already centred, e.g. one frame per screen.
 */
(function () {
  'use strict';

  /* Page scroll used per pixel of sideways travel. Above 1 the glide is
     slower and more deliberate than the scroll that drives it. */
  var SCROLL_RATIO = 1.6;

  function reducedMotion() {
    try {
      return window.matchMedia &&
             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { return false; }
  }

  function touchPrimary() {
    try {
      return window.matchMedia &&
             window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    } catch (e) { return false; }
  }

  var SEPARATE = (function () {
    try { return !!(window.CSS && CSS.supports && CSS.supports('translate', '1px 0px')); }
    catch (e) { return false; }
  })();

  function smoothState() {
    return (window.__blsSmooth && window.__blsSmooth.state) || 'off';
  }

  function build() {
    var gsap = window.gsap, ST = window.ScrollTrigger;
    if (!gsap || !ST) return false;
    if (smoothState() === 'pending') return false;   // wait for that decision

    var section = document.querySelector('.popular-products-section');
    if (!section) return true;                 // not this page
    if (section.__inDemandPinned) return true; // already wired

    if (reducedMotion()) { section.__inDemandPinned = true; return true; }

    var wrapper = section.querySelector('.section__swiper-wrapper');
    var outer = wrapper && wrapper.closest('.swiper');
    if (!outer || !sw()) return false;

    /* Whatever carousel instance is current - see the header on rebuilds. */
    function sw() {
      var s = outer.swiper;
      return (s && !s.destroyed && typeof s.setTranslate === 'function') ? s : null;
    }

    var slides = outer.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
    if (slides.length < 2) return false;

    /* How far right the row starts so the first frame is centred. Measured
       on refresh only, never while scrolling - reading layout inside the
       per-frame update forces a layout flush every frame. */
    var offset = 0;
    function measure() {
      var W = outer.clientWidth;
      var s = slides[0].getBoundingClientRect().width;
      offset = (W > 0 && s > 0 && s < W) ? Math.round((W - s) / 2) : 0;
      return offset;
    }
    if (measure() <= 0) return false;          // carousel not settled yet - retry

    section.__inDemandPinned = true;

    var touch = touchPrimary();
    var lenisDrives = smoothState() === 'on' && !touch;
    var notched = smoothState() !== 'on' && !touch;
    try { gsap.registerPlugin(ST); } catch (e) {}

    var proxy = { p: 0 };
    var owned = false;
    var st = null;
    var first = sw();
    var hadWheel = !!(first.mousewheel && first.mousewheel.enabled);

    function at() { return offset * (1 - proxy.p); }

    function write(x) {
      if (SEPARATE) { wrapper.style.translate = x + 'px 0px'; return; }
      var s = sw();
      if (s) s.setTranslate(x);
    }

    function freeze() {
      var s = sw();
      if (!s) return;
      try {
        s.allowTouchMove = false;
        s.allowSlideNext = false;
        s.allowSlidePrev = false;
        if (s.mousewheel && s.mousewheel.disable) s.mousewheel.disable();
      } catch (e) {}
    }

    function unfreeze() {
      var s = sw();
      if (!s) return;
      try {
        s.allowTouchMove = true;
        s.allowSlideNext = true;
        s.allowSlidePrev = true;
        if (hadWheel && s.mousewheel && s.mousewheel.enable) s.mousewheel.enable();
        s.updateProgress();
        s.updateActiveIndex();
        s.updateSlidesClasses();
        if (s.navigation && s.navigation.update) s.navigation.update();
      } catch (e) {}
    }

    /* Re-apply everything we hold while the row is ours. Used after the theme
       rebuilds the carousel and after ScrollTrigger re-measures. */
    function reassert() {
      if (!owned) return;
      freeze();
      if (SEPARATE) wrapper.style.willChange = 'translate';
      write(at());
    }

    function takeOver() {
      if (owned) return;
      owned = true;
      /* If someone swiped the carousel after the hold and then scrolled back
         up, glide it home rather than jumping it there. */
      var s = sw();
      if (s && (s.activeIndex > 0 || Math.abs(s.translate) > 1)) {
        try { s.slideTo(0, 500); } catch (e) {}
      }
      reassert();
    }

    /* At the bottom the offset is 0 - the carousel's natural position - so
       handing back is just removing ours. Nothing moves. */
    function handBack() {
      if (!owned) return;
      owned = false;
      if (SEPARATE) {
        wrapper.style.translate = '';
        wrapper.style.willChange = '';
      } else {
        var s = sw();
        if (s) { s.setTransition(0); s.setTranslate(0); }
      }
      unfreeze();
    }

    var tween = gsap.to(proxy, {
      p: 1,
      ease: 'none',
      onUpdate: function () {
        /* Any position short of the end belongs to us. onToggle alone misses
           a jump from below the section to above it in one step (Home key,
           scrollbar drag, back-to-top): the trigger is never active, so the
           row would stay at the left edge and snap to centre when the hold
           next engaged. The scrub easing back toward 0 lands here instead,
           and the row glides back to centre while the section is off-screen. */
        if (!owned && proxy.p < 1) takeOver();
        if (owned) write(at());
        if (owned && st && st.isActive === false && proxy.p === 1) handBack();
      },
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        pin: true,
        pinSpacing: true,
        anticipatePin: lenisDrives ? 0 : 1,
        scrub: notched ? 1 : 0.2,
        invalidateOnRefresh: true,
        end: function () {
          return '+=' + Math.max(1, Math.round(measure() * SCROLL_RATIO));
        },
        onRefresh: function () {
          measure();
          reassert();
        },
        onToggle: function (self) {
          if (self.isActive) takeOver();
        }
      }
    });

    st = tween.scrollTrigger;

    window.addEventListener('resize', reassert);

    /* The first frame must already be centred as the section scrolls into
       view, not snap there when the hold engages, so take the row now.

       This is deliberately unconditional. An earlier version did it only
       `if (st.progress < 1)` - but a ScrollTrigger has not computed its
       progress yet at the moment it is created, so progress was undefined,
       `undefined < 1` is false, and the centring was silently skipped. If the
       page has actually loaded below the section, the scrub runs to the end
       on its first update and handBack() returns the row - nothing lost. */
    takeOver();

    return true;
  }

  var polling = false;
  function boot() {
    if (build() || polling) return;
    polling = true;
    var tries = 0;
    var timer = setInterval(function () {
      /* Do not wait for the smooth-scroll decision forever. */
      if (tries > 16 && window.__blsSmooth && window.__blsSmooth.state === 'pending') {
        window.__blsSmooth.state = 'off';
      }
      if (build() || ++tries > 40) {                    // give up after ~10s
        clearInterval(timer);
        polling = false;
      }
    }, 250);
  }

  var resizeTimer = null;
  function bootSoon() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(boot, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  window.addEventListener('resize', bootSoon);
  document.addEventListener('bls:smooth', boot);
  document.addEventListener('shopify:section:load', function () {
    var s = document.querySelector('.popular-products-section');
    if (s) s.__inDemandPinned = false;
    boot();
  });
})();
